import { useMemo, useState } from 'react'
import { useDbValue } from '../lib/useDbValue.js'
import DataGate from '../components/DataGate.jsx'
import { seasonStandings, seasonCsv } from './gg/ggSeason.js'
import { PbccHeroHeader } from '../components/PbccLayout.jsx'

// Live Order-of-Merit — accumulates by-place points across the club's real
// events (reuses ggSeason.seasonStandings, keyed by team name so a team links
// across events). Gross/Net toggle + CSV export. Reuses the admin chrome.
export default function SeasonOoM() {
  const { data, status, error, retry } = useDbValue('tournaments')
  const [net, setNet] = useState(false)

  const events = useMemo(() => Object.entries(data || {})
    .filter(([, t]) => t?.info && !t.info.isDemo && t.flights)
    .sort((a, b) => (a[1]?.info?.createdAt || 0) - (b[1]?.info?.createdAt || 0))
    .map(([id, t]) => ({ id, name: t.info?.name || id, tournament: t })), [data])

  const standings = useMemo(
    () => seasonStandings({ name: 'Season', events }, { net, keyBy: 'name' }),
    [events, net],
  )

  const download = () => {
    const blob = new Blob([seasonCsv(standings)], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'order-of-merit.csv'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  if (status && status !== 'ready') {
    return <div className="p-10"><DataGate status={status} error={error} retry={retry} label="Loading events…" /></div>
  }

  return (
    <div>
      <PbccHeroHeader variant="admin" />
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="card flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold">Order of Merit — {events.length} event{events.length === 1 ? '' : 's'}</h2>
          <div className="flex items-center gap-3">
            <div className="flex gap-1 p-1 rounded-lg bg-gray-100">
              {[['gross', false], ['net', true]].map(([label, v]) => (
                <button key={label} onClick={() => setNet(v)}
                  className={`px-3 py-1 rounded-md text-sm font-bold uppercase ${net === v ? 'bg-green-deep text-white' : 'text-gray-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            <button className="btn-secondary text-xs" disabled={standings.rows.length === 0} onClick={download}>⬇ CSV</button>
          </div>
        </div>

        <div className="card">
          {standings.rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No completed events yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-gray-500 border-b">
                  <tr>
                    <th className="text-left py-2 w-10">#</th>
                    <th className="text-left py-2">Team</th>
                    {standings.events.map((e) => <th key={e.id} className="text-right py-2 whitespace-nowrap">{e.name?.split('\n')[0]}</th>)}
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.rows.map((r, i) => (
                    <tr key={r.teamId} className="border-b last:border-0">
                      <td className="py-2 font-bold text-gray-500">{i + 1}</td>
                      <td className="py-2 font-semibold">{r.teamName}</td>
                      {r.perEvent.map((pe) => <td key={pe.eventId} className="py-2 text-right">{pe.points || '—'}</td>)}
                      <td className="py-2 text-right font-extrabold">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
